import { useState, useEffect, useRef } from 'react';
import { 
  Award, Check, X, AlertTriangle, ChevronRight, HelpCircle, 
  LifeBuoy, Users, Clock, Trophy, Star, Sparkles, Home, 
  ArrowRight, Play, MessageCircle, Volume2, VolumeX, Gift,
  Share2, Loader2, BookOpen, Brain, Heart, Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Main Game Component
export default function KBDKBEGame() {
  // Game state
  const { isAuthenticated, studentId, studentClass, studentName} = useAuth();
  const [gameType, setGameType] = useState("KBD"); // KBD or KBE
  const [gameStarted, setGameStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [lifelines, setLifelines] = useState({
    fiftyFifty: true,
    audiencePoll: true,
    askExpert: true,
  });
  const [timer, setTimer] = useState(30);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [showLifelineHelp, setShowLifelineHelp] = useState(false);
  const [eliminatedOptions, setEliminatedOptions] = useState<string[]>([]);
  const [showAudiencePoll, setShowAudiencePoll] = useState(false);
  const [showExpertAdvice, setShowExpertAdvice] = useState(false);
  const [username, setUsername] = useState("");
  const [nameEntered, setNameEntered] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [difficulty, setDifficulty] = useState("medium");
  const [showAnimation, setShowAnimation] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [animationText, setAnimationText] = useState("");
  const [isHintUsed, setIsHintUsed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState("default");

  // References
  // Make sure timerBarRef is properly typed
  const timerBarRef = useRef<HTMLDivElement>(null);
  const confettiRef = useRef(null);
  
  // Fix for themes[selectedTheme] error (Line 71)
  // Define type for themes
  interface ThemeColors {
    primary: string;
    secondary: string;
    header: string;
    accent: string;
    textAccent: string;
  }

  interface Themes {
    [key: string]: ThemeColors;
  }
  // Define theme colors
  const themes: Themes = {
    default: {
      primary: "from-purple-800 to-blue-900",
      secondary: "from-purple-600 to-blue-600",
      header: "from-purple-800 to-blue-800",
      accent: "purple",
      textAccent: "text-purple-800"
    },
    medical: {
      primary: "from-cyan-800 to-teal-900",
      secondary: "from-cyan-600 to-teal-600",
      header: "from-cyan-800 to-teal-800",
      accent: "cyan",
      textAccent: "text-cyan-800"
    },
    tech: {
      primary: "from-blue-800 to-indigo-900",
      secondary: "from-blue-600 to-indigo-600",
      header: "from-blue-800 to-indigo-800",
      accent: "blue",
      textAccent: "text-blue-800"
    }
  };
  
  const currentTheme = themes[selectedTheme as keyof typeof themes] || themes.default;
  // Mock leaderboard data
  const leaderboardData = [
    { name: "Priya Singh", score: 50000, badge: "🏆" },
    { name: "Rahul Sharma", score: 40000, badge: "🥈" },
    { name: "Aditya Patel", score: 30000, badge: "🥉" },
    { name: "Meera Joshi", score: 20000, badge: "" },
    { name: "Vikram Rao", score: 10000, badge: "" },
  ];
  

  // Fix for questionsData[gameType] errors (Line 212)
  // Define proper types for your question data
  interface Question {
    id: number;
    question: string;
    options: string[];
    correctAnswer: string;
    level: number;
    points: number;
    hint: string;
  }

  interface GameQuestions {
    easy: Question[];
    medium: Question[];
    hard: Question[];
  }

  interface QuestionsData {
    [key: string]: GameQuestions;
  }


  // Mock questions with difficulty levels
  const questionsData: QuestionsData = {
    KBD: {
      easy: [
        {
          id: 1,
          question: "Which of the following is responsible for pumping blood through the body?",
          options: ["Lungs", "Heart", "Kidneys", "Liver"],
          correctAnswer: "Heart",
          level: 1,
          points: 1000,
          hint: "This organ beats approximately 100,000 times per day."
        },
        {
          id: 2,
          question: "The deficiency of which vitamin causes Rickets?",
          options: ["Vitamin A", "Vitamin B", "Vitamin C", "Vitamin D"],
          correctAnswer: "Vitamin D",
          level: 1,
          points: 1000,
          hint: "This vitamin is produced in the skin when exposed to sunlight."
        }
      ],
      medium: [
        {
          id: 1,
          question: "Which of the following is NOT a type of white blood cell?",
          options: ["Neutrophils", "Melanocytes", "Eosinophils", "Lymphocytes"],
          correctAnswer: "Melanocytes",
          level: 2,
          points: 2000,
          hint: "This cell type is associated with skin pigmentation."
        },
        {
          id: 2,
          question: "Insulin is produced by which part of the human body?",
          options: ["Liver", "Pancreas", "Kidney", "Spleen"],
          correctAnswer: "Pancreas",
          level: 2,
          points: 2000,
          hint: "This organ produces both digestive enzymes and hormones."
        }
      ],
      hard: [
        {
          id: 1,
          question: "Which hormone regulates calcium and phosphorus metabolism?",
          options: ["Thyroxine", "Calcitonin", "Insulin", "Adrenaline"],
          correctAnswer: "Calcitonin",
          level: 3,
          points: 5000,
          hint: "This hormone is produced by the C cells of the thyroid gland."
        },
        {
          id: 2,
          question: "What is the normal pH range of human blood?",
          options: ["5.5 - 6.5", "6.8 - 7.2", "7.35 - 7.45", "8.0 - 8.5"],
          correctAnswer: "7.35 - 7.45",
          level: 3,
          points: 5000,
          hint: "Blood pH is slightly basic, and must be maintained within a narrow range for survival."
        }
      ]
    },
    KBE: {
      easy: [
        {
          id: 1,
          question: "Which of the following is the correct symbol for logical AND?",
          options: ["||", "&&", "##", "++"],
          correctAnswer: "&&",
          level: 1,
          points: 1000,
          hint: "This symbol consists of two identical characters."
        },
        {
          id: 2,
          question: "What HTML tag is used to create a hyperlink?",
          options: ["<link>", "<a>", "<href>", "<url>"],
          correctAnswer: "<a>",
          level: 1,
          points: 1000,
          hint: "This tag is short for 'anchor'."
        }
      ],
      medium: [
        {
          id: 1,
          question: "Which data structure follows the LIFO principle?",
          options: ["Queue", "Stack", "Linked List", "Binary Tree"],
          correctAnswer: "Stack",
          level: 2,
          points: 2000,
          hint: "Think about a stack of plates - the last one placed is the first one removed."
        },
        {
          id: 2,
          question: "What is the time complexity of binary search?",
          options: ["O(n)", "O(n²)", "O(log n)", "O(n log n)"],
          correctAnswer: "O(log n)",
          level: 2,
          points: 2000,
          hint: "This algorithm divides the search interval in half each time."
        }
      ],
      hard: [
        {
          id: 1,
          question: "Which protocol is used for secure communication over the web?",
          options: ["HTTP", "FTP", "HTTPS", "SMTP"],
          correctAnswer: "HTTPS",
          level: 3,
          points: 5000,
          hint: "This protocol uses TLS/SSL encryption."
        },
        {
          id: 2,
          question: "What is the primary purpose of normalization in database design?",
          options: ["Improve query performance", "Reduce data redundancy", "Increase storage space", "Secure the database"],
          correctAnswer: "Reduce data redundancy",
          level: 3,
          points: 5000,
          hint: "This process involves organizing data to minimize duplication."
        }
      ]
    }
  };
  
  // Get questions based on game type and difficulty
  const getQuestions = () => {
    const baseQuestions = questionsData[gameType]?.[difficulty as keyof GameQuestions] || 
                          questionsData[gameType]?.medium;
    return baseQuestions.map((q: Question, index: number) => ({
      ...q,
      level: index + 1,
      points: difficulty === "easy" ? 1000 * (index + 1) : 
            difficulty === "medium" ? 5000 * (index + 1) : 10000 * (index + 1)
              // rest of your code
    }));
  };
  
  // Current questions
  const questions = getQuestions();
  
  // Current question based on index
  const currentQuestion = questions[currentQuestionIndex];
  
  // Reset game when switching between KBD and KBE
  useEffect(() => {
    resetGame();
  }, [gameType, difficulty]);
  
  // Timer effect
  // Fix for interval variable (Line 235)
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    
    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        // your code
      }, 1000);
      
      // Clear interval in return function
      return () => {
        if (interval) clearInterval(interval);
      };
    }
    // rest of your useEffect
  }, [isTimerRunning, timer]);

  

  // In your useEffect
  if (timerBarRef.current) {
    timerBarRef.current.style.transition = "width 1s linear";
  }
  
  // Handle timer expiration
  const handleTimeout = () => {
    playSound("timeout");
    setShowResult(true);
    setIsCorrect(false);
    setTimeout(() => {
      setGameOver(true);
    }, 2000);
  };
  
  // Start the game
  const startGame = () => {
    setGameStarted(true);
    setIsTimerRunning(true);
    playSound("start");
    
    // Show starting animation
    setAnimationText("Get Ready!");
    setShowAnimation(true);
    setTimeout(() => {
      setShowAnimation(false);
    }, 1500);
  };
  
  // Play sound effects
  // Fix for playSound function (Line 279)
  const playSound = (soundType: string) => {
    if (!soundEnabled) return;
    // Sound playing logic
  };
  
  // Handle option selection
  const handleOptionSelect = (option: string) => {
    if (showResult || selectedOption) return;
    setSelectedOption(option);

    setIsTimerRunning(false);
    setShowResult(true);
    
    const correct = option === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    
    if (correct) {
      playSound("correct");
      setScore(score + currentQuestion.points);
      
      // Show animation for correct answer
      setAnimationText(`+${currentQuestion.points}!`);
      setShowAnimation(true);
      setTimeout(() => {
        setShowAnimation(false);
      }, 1000);
      
      setTimeout(() => {
        setShowResult(false);
        setSelectedOption(null);
        setEliminatedOptions([]);
        setShowAudiencePoll(false);
        setShowExpertAdvice(false);
        setShowHint(false);
        setIsHintUsed(false);
        
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setTimer(30);
          setIsTimerRunning(true);
        } else {
          // Game completed successfully
          setGameOver(true);
          playSound("win");
        }
      }, 1500);
    } else {
      playSound("wrong");
      // Wrong answer
      setTimeout(() => {
        setGameOver(true);
      }, 1500);
    }
  };
  
  // Reset the game
  const resetGame = () => {
    setGameStarted(false);
    setCurrentQuestionIndex(0);
    setScore(0);
    setSelectedOption(null);
    setShowResult(false);
    setIsCorrect(false);
    setLifelines({
      fiftyFifty: true,
      audiencePoll: true,
      askExpert: true,
    });
    setTimer(30);
    setIsTimerRunning(false);
    setGameOver(false);
    setEliminatedOptions([]);
    setShowAudiencePoll(false);
    setShowExpertAdvice(false);
    setShowHint(false);
    setIsHintUsed(false);
  };
  

  


  // Use 50:50 lifeline
  const useFiftyFifty = () => {
    if (!lifelines.fiftyFifty || showResult) return;
    
    playSound("lifeline");
    const correctAnswer = currentQuestion.correctAnswer;
    let availableWrong = currentQuestion.options.filter((opt: string) => opt !== correctAnswer);
    
    // Randomly select two wrong options to eliminate
    const shuffled = availableWrong.sort(() => 0.5 - Math.random());
    const toEliminate = shuffled.slice(0, 2);
    
    setEliminatedOptions(toEliminate);
    setLifelines({ ...lifelines, fiftyFifty: false });
  };
  
  // Use Audience Poll lifeline
  const useAudiencePoll = () => {
    if (!lifelines.audiencePoll || showResult) return;
    
    playSound("lifeline");
    setShowAudiencePoll(true);
    setLifelines({ ...lifelines, audiencePoll: false });
  };
  
  // Use Ask the Expert lifeline
  const useAskExpert = () => {
    if (!lifelines.askExpert || showResult) return;
    
    playSound("lifeline");
    setShowExpertAdvice(true);
    setLifelines({ ...lifelines, askExpert: false });
  };
  
  // Use hint
  const useHint = () => {
    if (isHintUsed || showResult) return;
    
    playSound("hint");
    setShowHint(true);
    setIsHintUsed(true);
  };


  interface PercentageMap {
  [key: string]: number;
}
  
  // Generate audience poll percentages
  const getAudiencePollPercentages = () => {
    const correctAnswer = currentQuestion.correctAnswer;
    let percentages: PercentageMap = {};

    currentQuestion.options.forEach((option: string) => {
      if (eliminatedOptions.includes(option)) {
        percentages[option] = 0;
      } else if (option === correctAnswer) {
        percentages[option] = Math.floor(Math.random() * 30) + 50;
      } else {
        percentages[option] = Math.floor(Math.random() * 20);
      }
    });
    
    // Normalize to make sure they sum to 100%
    // Fix for Object.values(percentages).reduce (Line 418)
    const sum = Object.values(percentages).reduce((a: number, b: number) => a + b, 0);

    // Fix for Object.keys(percentages) (Line 421-422)
    Object.keys(percentages).forEach((key: string) => {
      percentages[key] = Math.round((percentages[key] / sum) * 100);
    });
    
    return percentages;
  };
  
  // Get expert advice
  const getExpertAdvice = () => {
    const correctAnswer = currentQuestion.correctAnswer;
    const randomConfidence = Math.random();
    
    if (randomConfidence > 0.2) {
      // Expert is confident and gives correct answer
      return `I'm quite confident the answer is ${correctAnswer}. Based on my expertise, this is the most logical choice.`;
    } else {
      // Expert is unsure but hints at correct answer
      const wrongOption = currentQuestion.options.find((opt: string) => 
        opt !== correctAnswer && !eliminatedOptions.includes(opt)
      );
      return `I'm not entirely certain, but I believe it's either ${correctAnswer} or ${wrongOption}. If I had to guess, I'd lean slightly toward ${correctAnswer}.`;
    }
  };
  
  // Format money amounts
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      // other options
    }).format(amount);
  };
  
  // Option styling
  const getOptionClass = (option: string) => {
    let baseClass = "p-4 rounded-lg border-2 flex items-center transition-all duration-300 ";
    
    if (eliminatedOptions.includes(option)) {
      return baseClass + "bg-gray-200 text-gray-500 opacity-50 cursor-not-allowed";
    }
    
    if (showResult) {
      if (option === currentQuestion.correctAnswer) {
        return baseClass + "bg-green-100 border-green-500 text-green-800 animate-pulse";
      } else if (option === selectedOption) {
        return baseClass + "bg-red-100 border-red-500 text-red-800 animate-pulse";
      }
    }
    
    if (option === selectedOption) {
      return baseClass + `bg-${currentTheme.accent}-100 border-${currentTheme.accent}-500 text-${currentTheme.accent}-800`;
    }
    
    return baseClass + `bg-${currentTheme.accent}-50 border-${currentTheme.accent}-300 hover:bg-${currentTheme.accent}-100 cursor-pointer transform hover:scale-105 transition-transform`;
  };
  
  // Handle name submission
  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim()) {
      setNameEntered(true);
      playSound("button");
    }
  };

  // Render audience poll results
  const renderAudiencePoll = () => {
    const percentages = getAudiencePollPercentages();
    
    return (
      <div className="bg-blue-50 p-4 rounded-lg mt-4 border border-blue-200 shadow-md animate-fadeIn">
        <h3 className="font-bold text-center mb-2 flex items-center justify-center">
          <Users size={18} className="mr-2 text-blue-600" />
          Audience Poll Results
        </h3>
        <div className="space-y-3">
          {currentQuestion.options.map(option => (
            <div key={option} className="flex items-center">
              <span className="w-24 font-medium truncate">{option}:</span>
              <div className="flex-1 bg-gray-200 rounded-full h-5 mx-2">
                <div 
                  className="bg-blue-500 h-5 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${percentages[option]}%` }}
                ></div>
              </div>
              <span className="w-12 text-right font-semibold">{percentages[option]}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render expert advice
  const renderExpertAdvice = () => {
    return (
      <div className="bg-yellow-50 p-4 rounded-lg mt-4 border border-yellow-200 shadow-md animate-fadeIn">
        <h3 className="font-bold flex items-center mb-2">
          <Trophy size={18} className="mr-2 text-yellow-600" />
          Expert's Advice
        </h3>
        <div className="flex items-start space-x-3">
          <div className="w-12 h-12 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
            <BookOpen size={24} className="text-yellow-700" />
          </div>
          <p className="italic bg-white p-3 rounded-lg border border-yellow-100 relative">
            {getExpertAdvice()}
            <div className="absolute left-0 top-4 -ml-2 w-2 h-2 bg-white transform rotate-45 border-l border-b border-yellow-100"></div>
          </p>
        </div>
      </div>
    );
  };
  
  // Render hint
  const renderHint = () => {
    return (
      <div className="bg-green-50 p-4 rounded-lg mt-4 border border-green-200 shadow-md animate-fadeIn">
        <h3 className="font-bold flex items-center mb-2">
          <HelpCircle size={18} className="mr-2 text-green-600" />
          Hint
        </h3>
        <p className="bg-white p-3 rounded-lg border border-green-100">{currentQuestion.hint}</p>
      </div>
    );
  };
  
  // Display animation
  const renderAnimation = () => {
    if (!showAnimation) return null;
    
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
        <div className="text-4xl font-bold text-white bg-purple-600 px-6 py-3 rounded-lg animate-bounce shadow-lg">
          {animationText}
        </div>
      </div>
    );
  };
  
  // Render leaderboard
  const renderLeaderboard = () => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-purple-800">Leaderboard</h2>
            <button 
              onClick={() => setShowLeaderboard(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Trophy size={24} className="text-yellow-500" />
              <h3 className="text-lg font-semibold">Top Players</h3>
            </div>
            
            <div className="space-y-3">
              {leaderboardData.map((player, index) => (
                <div 
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    index < 3 ? "bg-yellow-50" : "bg-gray-50"
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                      {index + 1}
                    </div>
                    <span className="font-medium">{player.name}</span>
                    {player.badge && <span className="ml-2">{player.badge}</span>}
                  </div>
                  <span className="font-bold">{formatMoney(player.score)}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-3">Keep playing to get on the leaderboard!</p>
            <button
              onClick={() => setShowLeaderboard(false)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Back to Game
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render name entry screen
  if (!nameEntered) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen bg-gradient-to-b ${currentTheme.primary} p-4`}>
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-blue-500"></div>
          
          <div className="text-center mb-6">
            <div className="mb-4">
              <Sparkles size={40} className="text-yellow-500 inline-block" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">Welcome to</h1>
            <div className="flex justify-center gap-2 mt-3">
              <span className="bg-purple-800 text-white px-3 py-1 rounded-md font-bold shadow-md">KBD</span>
              <span className="text-purple-800 font-bold">/</span>
              <span className="bg-blue-800 text-white px-3 py-1 rounded-md font-bold shadow-md">KBE</span>
            </div>
            <h2 className="mt-2 text-xl font-semibold text-gray-700">NAE Coaching</h2>
          </div>
          
          <form onSubmit={handleNameSubmit} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Enter Your Name
              </label>
              <input
                type="text"
                id="username"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your name here..."
                required
              />
            </div>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Choose Game Mode</p>
              <div className="flex space-x-4">
                <button
                  type="button"
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    gameType === "KBD" 
                      ? "bg-purple-600 text-white shadow-md transform scale-105" 
                      : "bg-white text-purple-600 border border-purple-600"
                  }`}
                  onClick={() => setGameType("KBD")}
                >
                  <div className="flex flex-col items-center">
                    <Heart size={20} className="mb-1" />
                    <span>Kaun Banega Doctor</span>
                  </div>
                </button>
                <button
                  type="button"
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    gameType === "KBE" 
                      ? "bg-blue-600 text-white shadow-md transform scale-105" 
                      : "bg-white text-blue-600 border border-blue-600"
                  }`}
                  onClick={() => setGameType("KBE")}
                >
                  <div className="flex flex-col items-center">
                    <Brain size={20} className="mb-1" />
                    <span>Kaun Banega Engineer</span>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Select Difficulty</p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                    difficulty === "easy" 
                      ? "bg-green-600 text-white shadow-md" 
                      : "bg-white text-green-600 border border-green-600"
                  }`}
                  onClick={() => setDifficulty("easy")}
                >
                  Easy
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                    difficulty === "medium" 
                      ? "bg-yellow-600 text-white shadow-md" 
                      : "bg-white text-yellow-600 border border-yellow-600"
                  }`}
                  onClick={() => setDifficulty("medium")}
                >
                  Medium
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                    difficulty === "hard" 
                      ? "bg-red-600 text-white shadow-md" 
                      : "bg-white text-red-600 border border-red-600"
                  }`}
                  onClick={() => setDifficulty("hard")}
                >
                  Hard
                </button>
              </div>
            </div>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">Theme</p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  className={`flex-1 py-2 rounded-lg font-medium transition-all ${
                    selectedTheme === "default" 
                      ? "bg-purple-600 text-white shadow-md" 
                      : "bg-white text-purple-600 border border-purple-600"
                  }`}
                  onClick={() => setSelectedTheme("default")}
                >
                  Default
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 rounded-lg font-medium transition-all ${selectedTheme === "medical" 
      ? "bg-cyan-600 text-white shadow-md" 
      : "bg-white text-cyan-600 border border-cyan-600"
  }`}
  onClick={() => setSelectedTheme("medical")}
>
  Medical
</button>
<button
  type="button"
  className={`flex-1 py-2 rounded-lg font-medium transition-all ${
    selectedTheme === "tech" 
      ? "bg-blue-600 text-white shadow-md" 
      : "bg-white text-blue-600 border border-blue-600"
  }`}
  onClick={() => setSelectedTheme("tech")}
>
  Tech
</button>
              </div>
            </div>
            
            <button
              type="submit"
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Start Game</span>
              <Play size={18} />
            </button>
          </form>
          
          <div className="mt-6 flex items-center justify-between text-gray-500 text-sm">
            <button
              onClick={() => setShowLeaderboard(true)}
              className="flex items-center hover:text-purple-600 transition-colors"
            >
              <Trophy size={16} className="mr-1" />
              Leaderboard
            </button>
            
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="flex items-center hover:text-purple-600 transition-colors"
            >
              {soundEnabled ? (
                <>
                  <Volume2 size={16} className="mr-1" />
                  Sound On
                </>
              ) : (
                <>
                  <VolumeX size={16} className="mr-1" />
                  Sound Off
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render game over screen
  if (gameOver) {
    return (
      <div className={`flex flex-col items-center justify-center min-h-screen bg-gradient-to-b ${currentTheme.primary} p-4`}>
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-purple-500 to-blue-500"></div>
          
          <div className="text-center mb-8">
            <div className="mb-4">
              {isCorrect ? (
                <Trophy size={60} className="text-yellow-500 inline-block" />
              ) : (
                <AlertTriangle size={60} className="text-red-500 inline-block" />
              )}
            </div>
            
            <h1 className="text-2xl font-bold text-gray-800">
              {isCorrect ? "Congratulations!" : "Game Over!"}
            </h1>
            
            <p className="text-lg font-medium text-gray-600 mt-2">
              {isCorrect 
                ? "You've completed all the questions!" 
                : "Better luck next time!"}
            </p>
            
            <div className="mt-6 bg-purple-50 rounded-lg p-4 flex justify-between items-center">
              <div className="text-left">
                <p className="text-sm font-medium text-gray-600">Your Score</p>
                <p className="text-2xl font-bold text-purple-800">{formatMoney(score)}</p>
              </div>
              
              <div className="bg-purple-100 rounded-full p-2">
                <Award size={36} className="text-purple-800" />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={resetGame}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center space-x-2"
            >
              <span>Play Again</span>
              <ArrowRight size={18} />
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowLeaderboard(true)}
                className="flex-1 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <Trophy size={18} className="mr-2 text-yellow-500" />
                Leaderboard
              </button>
              
              <button
                className="flex-1 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
                onClick={() => setNameEntered(false)}
              >
                <Home size={18} className="mr-2 text-purple-500" />
                Main Menu
              </button>
            </div>
            
            <div className="flex items-center justify-center">
              <button
                className="text-purple-600 underline text-sm flex items-center"
                onClick={() => {
                  // Share functionality would go here
                  console.log("Share results");
                }}
              >
                <Share2 size={14} className="mr-1" />
                Share Your Results
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render main game screen
  return (
    <div className={`flex flex-col min-h-screen bg-gradient-to-b ${currentTheme.primary}`}>
      {/* Header */}
      <div className={`p-4 bg-gradient-to-r ${currentTheme.header} text-white`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setNameEntered(false)}
              className="mr-4 hover:text-gray-200 transition-colors"
            >
              <Home size={24} />
            </button>
            
            <div>
              <h1 className="font-bold text-lg">
                {gameType === "KBD" ? "Kaun Banega Doctor" : "Kaun Banega Engineer"}
              </h1>
              <p className="text-sm opacity-80">Welcome, {username}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="hover:text-gray-200 transition-colors"
            >
              {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </button>
            
            <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1 text-sm font-bold">
              {formatMoney(score)}
            </div>
          </div>
        </div>
      </div>
      
      {/* Game area */}
      <div className="flex-1 p-4 flex flex-col">
        {/* Game info */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full bg-${currentTheme.accent}-100 flex items-center justify-center mr-2`}>
                <span className={`font-bold ${currentTheme.textAccent}`}>{currentQuestionIndex + 1}</span>
              </div>
              <span className="font-medium text-gray-700">Question {currentQuestionIndex + 1}/{questions.length}</span>
            </div>
            
            <div className="flex items-center">
              <Clock size={18} className="mr-1 text-gray-500" />
              <span className="font-medium text-gray-700">{timer}s</span>
            </div>
          </div>
          
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              ref={timerBarRef}
              className="h-full bg-gradient-to-r from-green-500 to-yellow-500"
              style={{ width: `${(timer / 30) * 100}%` }}
            ></div>
          </div>
        </div>
        
        {/* Question area */}
        {!gameStarted ? (
          <div className="bg-white rounded-lg shadow-md p-6 mb-4 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Ready to Play?</h2>
            <p className="text-gray-600 mb-6">Click the button below to start the game.</p>
            <button
              onClick={startGame}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center justify-center space-x-2 mx-auto"
            >
              <Play size={18} />
              <span>Start Game</span>
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-md p-5 mb-4">
              <p className="font-medium text-lg text-gray-800 leading-relaxed">
                {currentQuestion.question}
              </p>
            </div>
            
            {/* Options */}
            <div className="grid grid-cols-1 gap-3 mb-4">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={option}
                  className={getOptionClass(option)}
                  onClick={() => handleOptionSelect(option)}
                  disabled={eliminatedOptions.includes(option) || showResult}
                >
                  <div className={`w-8 h-8 rounded-full ${
                    showResult && option === currentQuestion.correctAnswer 
                      ? "bg-green-100 text-green-800" 
                      : showResult && option === selectedOption && option !== currentQuestion.correctAnswer 
                      ? "bg-red-100 text-red-800"
                      : `bg-${currentTheme.accent}-100 ${currentTheme.textAccent}`
                  } flex items-center justify-center mr-3 font-bold`}>
                    {String.fromCharCode(65 + index)}
                  </div>
                  <span className="flex-1 text-left">{option}</span>
                  {showResult && option === currentQuestion.correctAnswer && (
                    <Check size={24} className="text-green-500 ml-2" />
                  )}
                  {showResult && option === selectedOption && option !== currentQuestion.correctAnswer && (
                    <X size={24} className="text-red-500 ml-2" />
                  )}
                </button>
              ))}
            </div>
            
            {/* Show audience poll results */}
            {showAudiencePoll && renderAudiencePoll()}
            
            {/* Show expert advice */}
            {showExpertAdvice && renderExpertAdvice()}
            
            {/* Show hint */}
            {showHint && renderHint()}
            
            {/* Lifelines */}
            <div className="bg-white rounded-lg shadow-md p-4 mt-auto">
              <div className="flex justify-between items-center">
                <div className="flex space-x-3">
                  <button
                    onClick={useFiftyFifty}
                    className={`p-2 rounded-full ${
                      lifelines.fiftyFifty 
                        ? `bg-${currentTheme.accent}-100 ${currentTheme.textAccent} hover:bg-${currentTheme.accent}-200` 
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    disabled={!lifelines.fiftyFifty || showResult}
                  >
                    <div className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center">
                      <span className="font-bold text-sm">50:50</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={useAudiencePoll}
                    className={`p-2 rounded-full ${
                      lifelines.audiencePoll 
                        ? `bg-${currentTheme.accent}-100 ${currentTheme.textAccent} hover:bg-${currentTheme.accent}-200` 
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    disabled={!lifelines.audiencePoll || showResult}
                  >
                    <div className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center">
                      <Users size={18} />
                    </div>
                  </button>
                  
                  <button
                    onClick={useAskExpert}
                    className={`p-2 rounded-full ${
                      lifelines.askExpert 
                        ? `bg-${currentTheme.accent}-100 ${currentTheme.textAccent} hover:bg-${currentTheme.accent}-200` 
                        : "bg-gray-100 text-gray-400 cursor-not-allowed"
                    }`}
                    disabled={!lifelines.askExpert || showResult}
                  >
                    <div className="w-10 h-10 rounded-full border-2 border-current flex items-center justify-center">
                      <Trophy size={18} />
                    </div>
                  </button>
                </div>
                
                <div>
                  <button
                    onClick={useHint}
                    className={`px-4 py-2 rounded-lg flex items-center ${
                      isHintUsed 
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                        : `border border-${currentTheme.accent}-400 ${currentTheme.textAccent} hover:bg-${currentTheme.accent}-50`
                    }`}
                    disabled={isHintUsed || showResult}
                  >
                    <HelpCircle size={18} className="mr-1" />
                    <span>Hint</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Leaderboard Modal */}
      {showLeaderboard && renderLeaderboard()}
      
      {/* Display animations */}
      {renderAnimation()}
      
      {/* Lifeline help modal */}
      {showLifelineHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-purple-800">Lifelines</h2>
              <button 
                onClick={() => setShowLifelineHelp(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-purple-800 text-sm">50:50</span>
                </div>
                <div>
                  <h3 className="font-medium text-purple-800">Fifty-Fifty</h3>
                  <p className="text-gray-600 text-sm">Eliminates two incorrect options, making it easier to find the correct answer.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Users size={18} className="text-blue-800" />
                </div>
                <div>
                  <h3 className="font-medium text-blue-800">Audience Poll</h3>
                  <p className="text-gray-600 text-sm">Shows how the audience would vote on this question.</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                  <Trophy size={18} className="text-yellow-800" />
                </div>
                <div>
                  <h3 className="font-medium text-yellow-800">Ask an Expert</h3>
                  <p className="text-gray-600 text-sm">Get advice from a subject matter expert for this question.</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowLifelineHelp(false)}
              className="w-full mt-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}